const BASE = '/api/platform/v1';

import { isChatDebugEnabled, isChatDebugPath, chatDebugLog } from '@/lib/chat/chat-debug';

export class ApiError extends Error {
	constructor(
		readonly status: number,
		readonly code: string,
		message: string,
	) {
		super(message);
		this.name = 'ApiError';
	}
}

type RequestOpts = {
	signal?: AbortSignal;
	headers?: HeadersInit;
};

async function parseErrorResponse(res: Response): Promise<ApiError> {
	let code = 'unknown';
	let message = `Request failed (${res.status})`;
	try {
		const body = (await res.json()) as { code?: string; message?: string };
		if (body && typeof body === 'object') {
			if (typeof body.code === 'string') code = body.code;
			if (typeof body.message === 'string') message = body.message;
		}
	} catch {
		// ignore parse failure
	}
	return new ApiError(res.status, code, message);
}

async function request<T>(method: string, path: string, init: RequestInit): Promise<T> {
	const url = `${BASE}${path}`;
	const shouldDebug = isChatDebugEnabled() && isChatDebugPath(path);
	const startedAt = typeof performance !== 'undefined' ? performance.now() : Date.now();

	if (shouldDebug) {
		chatDebugLog('api', 'request:start', {
			method,
			path,
			url,
			hasBody: init.body !== undefined,
		});
	}

	const res = await fetch(url, init);

	if (!res.ok) {
		const error = await parseErrorResponse(res);
		if (shouldDebug) {
			const completedAt = typeof performance !== 'undefined' ? performance.now() : Date.now();
			chatDebugLog('api', 'request:error', {
				method,
				path,
				status: res.status,
				code: error.code,
				// The debug log keeps the server's text; the screen never shows it.
				// eslint-disable-next-line crewstack/no-server-error-text
				message: error.message,
				durationMs: Math.round(completedAt - startedAt),
			});
		}
		throw error;
	}

	if (shouldDebug) {
		const completedAt = typeof performance !== 'undefined' ? performance.now() : Date.now();
		chatDebugLog('api', 'request:success', {
			method,
			path,
			status: res.status,
			durationMs: Math.round(completedAt - startedAt),
		});
	}

	if (method === 'DELETE') {
		const text = await res.text();
		return (text ? JSON.parse(text) : undefined) as T;
	}

	return res.json() as Promise<T>;
}

export const apiClient = {
	async get<T>(path: string, opts?: RequestOpts): Promise<T> {
		return request<T>('GET', path, {
			method: 'GET',
			cache: 'no-store',
			signal: opts?.signal,
			headers: opts?.headers,
		});
	},

	async post<T>(path: string, body?: unknown, opts?: RequestOpts): Promise<T> {
		const hasBody = body !== undefined;
		return request<T>('POST', path, {
			method: 'POST',
			headers: {
				...(hasBody ? { 'Content-Type': 'application/json' } : {}),
				...(opts?.headers ?? {}),
			},
			body: hasBody ? JSON.stringify(body) : undefined,
			cache: 'no-store',
			signal: opts?.signal,
		});
	},

	async postForm<T>(path: string, body: FormData, opts?: RequestOpts): Promise<T> {
		return request<T>('POST', path, {
			method: 'POST',
			headers: {
				...(opts?.headers ?? {}),
			},
			body,
			cache: 'no-store',
			signal: opts?.signal,
		});
	},

	async put<T>(path: string, body?: unknown, opts?: RequestOpts): Promise<T> {
		const hasBody = body !== undefined;
		return request<T>('PUT', path, {
			method: 'PUT',
			headers: {
				...(hasBody ? { 'Content-Type': 'application/json' } : {}),
				...(opts?.headers ?? {}),
			},
			body: hasBody ? JSON.stringify(body) : undefined,
			cache: 'no-store',
			signal: opts?.signal,
		});
	},

	async patch<T>(path: string, body?: unknown, opts?: RequestOpts): Promise<T> {
		const hasBody = body !== undefined;
		return request<T>('PATCH', path, {
			method: 'PATCH',
			headers: {
				...(hasBody ? { 'Content-Type': 'application/json' } : {}),
				...(opts?.headers ?? {}),
			},
			body: hasBody ? JSON.stringify(body) : undefined,
			cache: 'no-store',
			signal: opts?.signal,
		});
	},

	async delete<T>(path: string, opts?: RequestOpts): Promise<T> {
		return request<T>('DELETE', path, {
			method: 'DELETE',
			cache: 'no-store',
			signal: opts?.signal,
			headers: opts?.headers,
		});
	},
};
